import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import apiService from '../services/api.service';

interface Role {
    roleid: number;
    rolename: string;
    displayname: string;
    description?: string;
    isactive: boolean;
    issystem: boolean;
    permissioncount?: number;
}

interface Permission {
    permissionkey: string;
    permissionname: string;
    category?: string;
    description?: string;
    isgranted?: boolean;
}

interface PermissionCategory {
    category: string;
    permissions: Permission[];
}

const RoleManagement: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<PermissionCategory[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [includeInactive, setIncludeInactive] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        rolename: '',
        displayname: '',
        description: '',
        isactive: true,
    });

    const loadRoles = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getRoles(includeInactive);
            setRoles(response.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load roles');
        } finally {
            setLoading(false);
        }
    }, [includeInactive]);

    const loadAvailablePermissions = useCallback(async () => {
        try {
            const response = await apiService.getAvailablePermissions();
            setAvailablePermissions(response.data || []);
        } catch (err: any) {
            console.error('Failed to load permissions:', err);
        }
    }, []);

    // Load roles and permissions on mount
    useEffect(() => {
        loadRoles();
        loadAvailablePermissions();
    }, [loadRoles, loadAvailablePermissions]);

    const handleOpenDialog = async (role?: Role) => {
        if (role) {
            // Edit mode
            setSelectedRole(role);
            setFormData({
                rolename: role.rolename,
                displayname: role.displayname,
                description: role.description || '',
                isactive: role.isactive,
            });

            // Load role permissions
            try {
                const response = await apiService.getRoleById(role.roleid);
                const roleWithPerms = response.data;
                const permKeys = roleWithPerms.permissions
                    .filter((p: any) => p.isgranted)
                    .map((p: any) => p.permissionkey);
                setSelectedPermissions(permKeys);
            } catch (err) {
                console.error('Failed to load role permissions:', err);
            }
        } else {
            // Create mode
            setSelectedRole(null);
            setFormData({
                rolename: '',
                displayname: '',
                description: '',
                isactive: true,
            });
            setSelectedPermissions([]);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedRole(null);
        setFormData({
            rolename: '',
            displayname: '',
            description: '',
            isactive: true,
        });
        setSelectedPermissions([]);
        setError(null);
    };

    const handlePermissionToggle = (permissionKey: string) => {
        setSelectedPermissions((prev) => {
            if (prev.includes(permissionKey)) {
                return prev.filter((p) => p !== permissionKey);
            } else {
                return [...prev, permissionKey];
            }
        });
    };

    const handleToggleCategory = (category: PermissionCategory) => {
        const categoryPermKeys = category.permissions.map((p) => p.permissionkey);
        const allSelected = categoryPermKeys.every((key) => selectedPermissions.includes(key));

        if (allSelected) {
            // Deselect all in category
            setSelectedPermissions((prev) => prev.filter((key) => !categoryPermKeys.includes(key)));
        } else {
            // Select all in category
            setSelectedPermissions((prev) => {
                const newSet = new Set([...prev, ...categoryPermKeys]);
                return Array.from(newSet);
            });
        }
    };

    const handleSave = async () => {
        try {
            setError(null);
            setLoading(true);

            const roleData = {
                ...formData,
                permissions: selectedPermissions,
            };

            if (selectedRole) {
                // Update existing role
                await apiService.updateRole(selectedRole.roleid, roleData);
                setSuccess('Role updated successfully');
            } else {
                // Create new role
                await apiService.createRole(roleData);
                setSuccess('Role created successfully');
            }

            handleCloseDialog();
            await loadRoles();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save role');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteDialog = (role: Role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    const handleDelete = async () => {
        if (!roleToDelete) return;

        try {
            setLoading(true);
            await apiService.deleteRole(roleToDelete.roleid);
            setSuccess(
                roleToDelete.issystem
                    ? 'Role deactivated successfully'
                    : 'Role deleted successfully'
            );
            handleCloseDeleteDialog();
            await loadRoles();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete role');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box component="section" aria-labelledby="role-management-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={{ xs: 1.5, md: 0 }}
                mb={3}
            >
                <Typography
                    component="h1"
                    id="role-management-heading"
                    variant="h4"
                    sx={{ fontSize: { xs: 22, sm: 26, md: 30 }, fontWeight: 700 }}
                >
                    Role Management
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                            />
                        }
                        label="Show Inactive"
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Create Role
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Display Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="center">Permissions</TableCell>
                            <TableCell align="center">Type</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((role) => (
                            <TableRow key={role.roleid}>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {role.issystem && <SecurityIcon fontSize="small" color="primary" />}
                                        <Typography>{role.rolename}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>{role.displayname}</TableCell>
                                <TableCell>{role.description || '-'}</TableCell>
                                <TableCell align="center">
                                    <Chip label={role.permissioncount || 0} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={role.issystem ? 'System' : 'Custom'}
                                        size="small"
                                        color={role.issystem ? 'primary' : 'default'}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={role.isactive ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={role.isactive ? 'success' : 'default'}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog(role)}
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDeleteDialog(role)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {roles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography color="text.secondary" py={3}>
                                        No roles found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Role Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedRole ? 'Edit Role' : 'Create New Role'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Role Name"
                                value={formData.rolename}
                                onChange={(e) =>
                                    setFormData({ ...formData, rolename: e.target.value })
                                }
                                disabled={selectedRole?.issystem}
                                required
                                helperText="Unique identifier for the role"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Display Name"
                                value={formData.displayname}
                                onChange={(e) =>
                                    setFormData({ ...formData, displayname: e.target.value })
                                }
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({ ...formData, description: e.target.value })
                                }
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isactive}
                                        onChange={(e) =>
                                            setFormData({ ...formData, isactive: e.target.checked })
                                        }
                                        disabled={selectedRole?.issystem}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>

                        {/* Permissions */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Permissions
                            </Typography>
                            {availablePermissions.map((category) => {
                                const categoryPermKeys = category.permissions.map(
                                    (p) => p.permissionkey
                                );
                                const selectedCount = categoryPermKeys.filter((key) =>
                                    selectedPermissions.includes(key)
                                ).length;
                                const allSelected = selectedCount === category.permissions.length;

                                return (
                                    <Accordion key={category.category}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={allSelected}
                                                        indeterminate={
                                                            selectedCount > 0 && !allSelected
                                                        }
                                                        onChange={() => handleToggleCategory(category)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                }
                                                label={`${category.category} (${selectedCount}/${category.permissions.length})`}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <FormGroup>
                                                {category.permissions.map((permission) => (
                                                    <FormControlLabel
                                                        key={permission.permissionkey}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedPermissions.includes(
                                                                    permission.permissionkey
                                                                )}
                                                                onChange={() =>
                                                                    handlePermissionToggle(
                                                                        permission.permissionkey
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {permission.permissionname}
                                                                </Typography>
                                                                {permission.description && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {permission.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                ))}
                                            </FormGroup>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading || !formData.rolename || !formData.displayname}
                    >
                        {selectedRole ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to {roleToDelete?.issystem ? 'deactivate' : 'delete'}{' '}
                        the role <strong>{roleToDelete?.displayname}</strong>?
                    </Typography>
                    {roleToDelete?.issystem && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            System roles cannot be deleted, they will be deactivated instead.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        {roleToDelete?.issystem ? 'Deactivate' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoleManagement;
